import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'src/utils/axios';
import {
    Container, Card, CardHeader, CardContent, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, IconButton, Tooltip, Typography, Box, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Slide
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DraftsIcon from '@mui/icons-material/Drafts';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const TURKISH_MONTHS = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const DraftList = () => {
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // YENİ: Profesyonel UI State'leri
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    useEffect(() => {
        fetchDrafts();
    }, []);

    const fetchDrafts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('GetDraftBulletins');
            setDrafts(response.data || []);
        } catch (err) {
            console.error("Taslaklar çekilirken hata oluştu", err);
            setError("Taslaklarınız yüklenirken bir hata meydana geldi.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialog({ ...confirmDialog, open: false });
    };

    // SİLME TALEBİNİ DİALOG'A GÖNDEREN FONKSİYON
    const requestDelete = (id, sayi, konu) => {
        const mesaj = (sayi || konu) ? `${sayi || konu}` : 'Bu isimsiz taslağı';
        setConfirmDialog({
            open: true,
            title: 'Taslağı Sil',
            message: `${mesaj} kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
            onConfirm: () => executeDelete(id)
        });
    };

    // ASIL SİLME İŞLEMİNİ YAPAN FONKSİYON
    const executeDelete = async (id) => {
        handleCloseConfirmDialog(); // Pop-up'ı kapat
        try {
            const response = await axios.get(`DeleteDraftBulletin?id=${id}`);
            const artemisResult = response.data;

            if (artemisResult.ErrorCode === "0") {
                setSnackbar({ open: true, message: artemisResult.ErrorDescription || 'Taslak başarıyla silindi.', severity: 'success' });
                setDrafts(drafts.filter(d => d.ID !== id));
            } else {
                setSnackbar({ open: true, message: `Hata: ${artemisResult.ErrorDescription}`, severity: 'error' });
            }
        } catch (err) {
            console.error("Silme işlemi başarısız", err);
            setSnackbar({ open: true, message: 'Silme işlemi sırasında bir hata oluştu.', severity: 'error' });
        }
    };

    const handleEditDraft = (draftRow) => {
        navigate('/CreateBulletin', {
            state: { draftData: draftRow }
        });
    };

    return (
        <Container maxWidth="xl" style={{ paddingTop: '24px', paddingBottom: '24px' }}>
            <Card>
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center">
                            <DraftsIcon color="warning" sx={{ mr: 1, fontSize: 30 }} />
                            <Typography variant="h5" fontWeight="bold">Taslak Bültenlerim</Typography>
                        </Box>
                    }
                    subheader="Henüz yayınlanmamış ve üzerinde çalışmaya devam ettiğiniz taslaklar. 3 gün işlem yapılmayan taslaklar size uyarı gönderildikten sonra sistem tarafından otomatik silinir."
                />
                <Divider />
                <CardContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <TableContainer component={Paper} variant="outlined">
                        <Table sx={{ minWidth: 650 }} aria-label="taslaklar tablosu">
                            <TableHead sx={{ backgroundColor: '#fff8e1' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Taslak Kodu / Sayı</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Konu</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Ürün Grupları</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Bülten Tarihi</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Durum</TableCell>
                                    <TableCell align='center' sx={{ fontWeight: 'bold', minWidth: '120px' }}>İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={30} sx={{ my: 2 }} /></TableCell></TableRow>
                                ) : drafts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                                                Henüz oluşturulmuş bir taslağınız bulunmuyor.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    drafts.map((row) => {
                                        const bultenTarihiObj = row.BULTEN_TARIHI ? new Date(row.BULTEN_TARIHI) : null;

                                        return (
                                            <TableRow key={row.ID} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold" color={row.SAYI ? 'textPrimary' : 'textSecondary'}>
                                                        {row.SAYI || 'Belirtilmemiş'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.BULTEN_KATEGORI || <Typography variant="caption" color="textSecondary">Belirtilmemiş</Typography>}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {row.URUN_GRUPLARI || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {bultenTarihiObj
                                                        ? `${bultenTarihiObj.getDate()} ${TURKISH_MONTHS[bultenTarihiObj.getMonth() + 1]} ${bultenTarihiObj.getFullYear()}`
                                                        : '-'
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label="Taslak" color="warning" size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title="Düzenlemeye Devam Et">
                                                        <IconButton color="primary" onClick={() => handleEditDraft(row)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Taslağı Sil">
                                                        <IconButton color="error" onClick={() => requestDelete(row.ID, row.SAYI, row.BULTEN_KATEGORI)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* SİLME ONAY DİALOG PENCERESİ */}
            <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
                    <WarningAmberIcon color="error" /> {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseConfirmDialog} color="inherit" variant="text">İptal</Button>
                    <Button onClick={confirmDialog.onConfirm} color="error" variant="contained" disableElevation>
                        Evet, Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* SNACKBAR (UYARILAR) */}
            <Snackbar open={snackbar.open} autoHideDuration={null} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} TransitionComponent={(props) => <Slide {...props} direction="down" />}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', boxShadow: 3 }} style={{ backgroundColor: snackbar.severity === 'error' ? '#c62828' : snackbar.severity === 'success' ? '#2e7d32' : undefined, color: '#fff' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Container>
    );
};

export default DraftList;